const byId = (baseUrl, id) => `${baseUrl}/${id}`;

export const createSalesEndpoints = (apiUrls = {}) => ({
  auth: {
    login: `${apiUrls.spAuth}/login`,
    changePassword: `${apiUrls.spAuth}/change-password`,
    resetPassword: `${apiUrls.spAuth}/reset-password`,
    resetUserPassword: `${apiUrls.spAuth}/reset-user-password`,
  },
  users: {
    base: apiUrls.spUsers,
    byId: (id) => byId(apiUrls.spUsers, id),
    checkUniqueEmail: (id, email) => `${apiUrls.spUsers}/unique/${id}/${email}`,
  },
  roles: {
    base: apiUrls.spRoles,
    byId: (id) => byId(apiUrls.spRoles, id),
  },
  userRoles: {
    base: apiUrls.spUserRoles,
    byId: (id) => byId(apiUrls.spUserRoles, id),
    byUser: (userId) => `${apiUrls.spUserRoles}/user/${userId}`,
  },
  saleRepresentatives: {
    base: apiUrls.spSaleRepresentatives,
    byId: (id) => byId(apiUrls.spSaleRepresentatives, id),
  },
  companies: {
    base: apiUrls.spCompanies,
    byId: (id) => byId(apiUrls.spCompanies, id),
  },
  contacts: {
    base: apiUrls.spContacts,
    byId: (id) => byId(apiUrls.spContacts, id),
    byCompany: (companyId) => `${apiUrls.spContacts}/company/${companyId}`,
  },
  customers: {
    base: apiUrls.spCustomers,
    byId: (id) => byId(apiUrls.spCustomers, id),
  },
  projects: {
    base: apiUrls.spProjects,
    byId: (id) => byId(apiUrls.spProjects, id),
    byCompany: (companyId) => `${apiUrls.spProjects}/company/${companyId}`,
    bySaleRep: (saleRepId) => `${apiUrls.spProjects}/sale-rep/${saleRepId}`,
  },
  salesQuotes: {
    base: apiUrls.spSaleQuotes,
    byId: (id) => byId(apiUrls.spSaleQuotes, id),
    byUser: (userId) => `${apiUrls.spSaleQuotes}/created-by/${userId}`,
    byProject: (projectId) => `${apiUrls.spSaleQuotes}/project/${projectId}`,
    active: `${apiUrls.spSaleQuotes}/active`,
    versions: (saleQuoteId) => `${apiUrls.spSaleQuotes}/${saleQuoteId}/versions`,
    createVersion: (id) => `${apiUrls.spSaleQuotes}/${id}/version`,
    updateStatus: (saleQuoteId) => `${apiUrls.spSaleQuotes}/${saleQuoteId}/status`,
    rebase: (id) => `${apiUrls.spSaleQuotes}/${id}/rebase`,
    autopick: `${apiUrls.spSaleQuotes}/autopick`,
    autopickParts: `${apiUrls.spSaleQuotes}/autopick-parts`,
    generateModelNumber: `${apiUrls.spSaleQuotes}/model-number`,
    electricalEnclosure: `${apiUrls.spSaleQuotes}/electrical-enclosure`,
    fusedDiscSize: `${apiUrls.spSaleQuotes}/fused-disc-size`,
    updateSelectedOptions: `${apiUrls.spSaleQuotes}/selected-options`,
    updateCostSummary: `${apiUrls.spSaleQuotes}/cost-summary`,
    warnings: `${apiUrls.spSaleQuotes}/warnings`,
    generateProposal: `${apiUrls.spSaleQuotes}/proposal`,
  },
  salesQuoteOrders: {
    base: apiUrls.spSalesQuoteOrder,
    byId: (id) => byId(apiUrls.spSalesQuoteOrder, id),
    create: `${apiUrls.spSalesQuoteOrder}/create`,
    delete: (id, saleQuoteId) => `${apiUrls.spSalesQuoteOrder}/${id}/${saleQuoteId}`,
  },
  saleQuoteWarnings: {
    base: apiUrls.spSaleQuoteWarnings,
    byId: (id) => byId(apiUrls.spSaleQuoteWarnings, id),
    bySaleQuote: (saleQuoteId) => `${apiUrls.spSaleQuoteWarnings}/sale-quote/${saleQuoteId}`,
  },
  selectedOptions: {
    base: apiUrls.spSelectedOptions,
    byId: (id) => byId(apiUrls.spSelectedOptions, id),
    bySaleQuote: (saleQuoteId) => `${apiUrls.spSelectedOptions}/sale-quote/${saleQuoteId}`,
  },
  costSummaries: {
    base: apiUrls.spCostSummaries,
    byId: (id) => byId(apiUrls.spCostSummaries, id),
    bySaleQuote: (saleQuoteId) => `${apiUrls.spCostSummaries}/sale-quote/${saleQuoteId}`,
  },
  pumps: {
    base: apiUrls.spPumps,
    byId: (id) => byId(apiUrls.spPumps, id),
    byType: (type) => `${apiUrls.spPumps}/type/${type}`,
  },
  pumpConfigurations: {
    base: apiUrls.spPumpConfigurations,
    byId: (id) => byId(apiUrls.spPumpConfigurations, id),
    bySaleQuote: (saleQuoteId) => `${apiUrls.spPumpConfigurations}/sale-quote/${saleQuoteId}`,
  },
  pumpsReference: {
    base: apiUrls.spPumpsReference,
    byId: (id) => byId(apiUrls.spPumpsReference, id),
  },
  stations: {
    base: apiUrls.spStations,
    byId: (id) => byId(apiUrls.spStations, id),
    active: `${apiUrls.spStations}/active`,
  },
  stationTypes: {
    base: apiUrls.spStationTypes,
    byId: (id) => byId(apiUrls.spStationTypes, id),
  },
  stationTypeConfigs: {
    base: apiUrls.stationTypeConfigs,
    byId: (id) => byId(apiUrls.stationTypeConfigs, id),
  },
  categories: {
    base: apiUrls.spCategories,
    byId: (id) => byId(apiUrls.spCategories, id),
    byFilter: (type, stationId, configId, primeSystemId) =>
      `${apiUrls.spCategories}/${type}/${stationId}/${configId}/${primeSystemId}`,
  },
  categoryMappings: {
    base: apiUrls.spCategoryMappings,
    byId: (id) => byId(apiUrls.spCategoryMappings, id),
  },
  parts: {
    base: apiUrls.spParts,
    byId: (id) => byId(apiUrls.spParts, id),
    byCategory: (categoryId, projectId) => `${apiUrls.spParts}/category/${categoryId}/${projectId}`,
    uploadPrice: `${apiUrls.spParts}/upload-price`,
  },
  partMappings: {
    base: apiUrls.spPartMappings,
    byId: (id) => byId(apiUrls.spPartMappings, id),
    paged: (page, catId, size) => `${apiUrls.spPartMappings}/${page}/${catId}/${size}`,
  },
  lookups: {
    base: apiUrls.spLookups,
    byId: (id) => byId(apiUrls.spLookups, id),
    byType: (typeId) => `${apiUrls.spLookups}/type/${typeId}`,
    byTypeName: (name) => `${apiUrls.spLookups}/type-name/${name}`,
    saleQuoteData: `${apiUrls.spLookups}/salequotedata`,
    voltage: `${apiUrls.spLookups}/voltage`,
    motorHp: `${apiUrls.spLookups}/motorhp`,
  },
  lookupTypes: {
    base: apiUrls.spLookupTypes,
    byId: (id) => byId(apiUrls.spLookupTypes, id),
    byName: (name) => `${apiUrls.spLookupTypes}/name/${name}`,
  },
  rules: {
    base: apiUrls.spRules,
    byId: (id) => byId(apiUrls.spRules, id),
    byTypeId: (typeId) => `${apiUrls.spRules}/type/${typeId}`,
    runAutopickValidations: `${apiUrls.spRules}/autopick-validations`,
    validateByWorkflow: `${apiUrls.spRules}/validate-workflow`,
    validateJson: `${apiUrls.spRules}/validate-json`,
  },
  autoPickPartsRules: {
    base: apiUrls.spAutoPickPartsRules,
    byId: (id) => byId(apiUrls.spAutoPickPartsRules, id),
  },
  configurations: {
    base: apiUrls.spConfigurations,
    byId: (id) => byId(apiUrls.spConfigurations, id),
  },
  primeSystems: {
    base: apiUrls.spPrimeSystems,
    byId: (id) => byId(apiUrls.spPrimeSystems, id),
  },
  productFamilies: {
    base: apiUrls.spProductFamilies,
    byId: (id) => byId(apiUrls.spProductFamilies, id),
  },
  productFamilyConfigurations: {
    base: apiUrls.spProductFamilyConfigurations,
    byId: (id) => byId(apiUrls.spProductFamilyConfigurations, id),
    byFilter: (stationId, configId, primeSystemId) =>
      `${apiUrls.spProductFamilyConfigurations}/${stationId}/${configId}/${primeSystemId}`,
  },
  systemConfigurations: {
    base: apiUrls.spSystemConfigurations,
    byId: (id) => byId(apiUrls.spSystemConfigurations, id),
  },
  verticalTurbineConfigurations: {
    base: apiUrls.spVerticalTurbineConfigurations,
    byId: (id) => byId(apiUrls.spVerticalTurbineConfigurations, id),
    byTypeId: (typeId) => `${apiUrls.spVerticalTurbineConfigurations}/type/${typeId}`,
    updateStandard: `${apiUrls.spVerticalTurbineConfigurations}/standard`,
  },
  disconnectTable: {
    base: apiUrls.spDisconnectTable,
    byId: (id) => byId(apiUrls.spDisconnectTable, id),
  },
  enclosurePricings: {
    base: apiUrls.spEnclosurePricings,
    byId: (id) => byId(apiUrls.spEnclosurePricings, id),
    byDimensions: (length, width) => `${apiUrls.spEnclosurePricings}/${length}/${width}`,
    byDimensionsAndType: (length, width, enclosureType) =>
      `${apiUrls.spEnclosurePricings}/${length}/${width}/${enclosureType}`,
  },
  hpVoltageAmp: {
    base: apiUrls.spHpVoltageAmp,
    byId: (id) => byId(apiUrls.spHpVoltageAmp, id),
  },
  motorFla: {
    base: apiUrls.spMotorFla,
    byId: (id) => byId(apiUrls.spMotorFla, id),
  },
  termAndConditions: {
    base: apiUrls.spTermAndConditions,
    byId: (id) => byId(apiUrls.spTermAndConditions, id),
    deleteWithHeading: (id) => `${apiUrls.spTermAndConditions}/${id}/delete-with-heading`,
  },
  termsHeadings: {
    base: apiUrls.spTermsHeading,
    byId: (id) => byId(apiUrls.spTermsHeading, id),
  },
  masterStationList: {
    base: apiUrls.spMasterStationList,
  },
  configSections: {
    base: apiUrls.spConfigSections,
  },
  configSubSections: {
    base: apiUrls.spConfigSubSections,
  },
  configFields: {
    base: apiUrls.spConfigFields,
  },
  advancedConfigRules: { base: apiUrls.spAdvancedConfigRules },
  pricingFormulas: { base: apiUrls.spPricingFormulas },
  configFieldValues: {
    base: apiUrls.spConfigFieldValues,
  },
  configValueRules: {
    base: apiUrls.spConfigValueRules,
  },
  configCoverage: {
    base: apiUrls.spConfigCoverage,
  },
  configSectionRules: {
    base: apiUrls.spConfigSectionRules,
  },
  pumpTableConfig: {
    base: apiUrls.spPumpTableConfig,
  },
  pumpTableConfigRules: {
    base: apiUrls.spPumpTableConfigRules,
  },
  catalogParts: {
    base: apiUrls.spCatalogParts,
  },
  partStations: {
    base: apiUrls.spPartStations,
  },
  partMatchRules: {
    base: apiUrls.spPartMatchRules,
  },
});
